import React, { useState } from 'react';
import { IStorefrontQuestion } from '../../models/Storefront';
import DataField from "../Common/DataField";
import ToggleField from "../Common/ToggleField";
import {IconButton} from "@fluentui/react";
import {DynamicSetting} from "../Common/Setting";
import ConditionalSetting from "../Common/ConditionalSetting";

interface StorefrontQuestionEditorProps {
  question?: IStorefrontQuestion;
  onChange: (question: IStorefrontQuestion) => void;
  canEdit: boolean;
  /**
   * This editor renders in five different screens (per-storefront Order Questions, org-wide Order
   * Questions, per-storefront Quote Request Questions, the product details modal, and the bundle
   * editor's Questions tab), so a fixed id would collide across them - the same trap the SMTP From
   * fields fell into. The host passes its own family prefix (e.g. `storefront.orderQuestion`), and
   * the question's own id (dynamic, since questions are tenant data) completes it via
   * DynamicSetting: `<prefix>.<questionId>.<field>`.
   *
   * Omitted where the host screen itself cannot be reached by a link (the product questions modal
   * opens from local state with no URL), since a mark that can never resolve is worse than none.
   */
  settingKeyPrefix?: string;
}

// A small wrapper over DynamicSetting, per the settings README: add it to INSTRUMENTED_PATTERN in
// scripts/settingsCoverage.js (alongside StyleField) or this file counts as uninstrumented despite
// every field being marked.
//
// Must stay at module scope: declared inside the editor it becomes a new component type on every
// render, so React remounts the wrapped field on each keystroke and the input loses focus.
const QuestionSetting: React.FC<{ prefix?: string; field: string; children: React.ReactElement }> = ({ prefix, field, children }) =>
    prefix ? <DynamicSetting id={`${prefix}.${field}`}>{children}</DynamicSetting> : children;

const StorefrontQuestionEditor: React.FC<StorefrontQuestionEditorProps> = ({ question, onChange, canEdit, settingKeyPrefix }) => {
  const [newAnswer, setNewAnswer] = useState('');

  const handleChange = (key: keyof IStorefrontQuestion, value: any) => {
      onChange({ ...question, [key]: value });
  };

  const handleAddAnswer = () => {
    if (newAnswer.trim()) {
        onChange({ ...question, answers: [...(question?.answers || []), newAnswer.trim()] });
        setNewAnswer('');
    }
  };

  const handleRemoveAnswer = (idx: number) => {
      onChange({ ...question, answers: (question?.answers || []).filter((_, i) => i !== idx) });
  };

  // A brand-new ad-hoc question has no id until the first save, so there is nothing to link to yet
  // - same as a banner slide before it is saved. Falls back to rendering unmarked rather than
  // synthesizing an id that would not match the one the question gets once saved.
  const settingPrefix = (settingKeyPrefix && question?.id) ? `${settingKeyPrefix}.${question.id}` : undefined;

  return (
    <div style={{ padding: 16 }}>
        {question?.entityId === 'global' &&
        <div>
            <QuestionSetting prefix={settingPrefix} field='default'><ToggleField disabled={!canEdit} label={'Default For New Storefronts'} inline checked={!!question?.default} onChange={(_, v) => handleChange('default', v)} /></QuestionSetting>
        </div>}
        <div>
            <QuestionSetting prefix={settingPrefix} field='isRequired'><ToggleField disabled={!canEdit} label={'Required'} inline checked={!!question?.isRequired} onChange={(_, v) => handleChange('isRequired', v)} /></QuestionSetting>
        </div>
        <div>
            <QuestionSetting prefix={settingPrefix} field='identifier'><DataField dataTestId='question-identifier' placeholder={'Enter a unique question identifier'} disabled={!canEdit} label={'Identifier'} required={true} value={question?.identifier || ''} onChange={(_, v) => handleChange('identifier', v)} /></QuestionSetting>
        </div>
        <div>
            <QuestionSetting prefix={settingPrefix} field='questionText'><DataField dataTestId='question-text' placeholder={'Enter Checkout Question'} disabled={!canEdit} required label={'Question Text'} multiline={true} value={question?.questionText || ''} onChange={(_, v) => handleChange('questionText', v)} /></QuestionSetting>
        </div>
        <div>
        <QuestionSetting prefix={settingPrefix} field='category'><DataField dataTestId='question-category' disabled={!canEdit} label={'Category'} value={question?.category || ''} onChange={(_, v) => handleChange('category', v)} /></QuestionSetting>
        </div>

        <ConditionalSetting
            when={!!(question?.answers && question.answers.length > 1)}
            idPrefix={(settingKeyPrefix && question?.id) ? `${settingKeyPrefix}.${question.id}.allowMultiSelect` : undefined}
        >
            <div>
                <QuestionSetting prefix={settingPrefix} field='allowMultiSelect'><ToggleField disabled={!canEdit} label={'Allow Multi-Select'} inline checked={!!question?.allowMultiSelect} onChange={(_, v) => handleChange('allowMultiSelect', v)} /></QuestionSetting>
            </div>
        </ConditionalSetting>
        <div>
            <label>Answers:</label>
            <ul>
              {(question?.answers || []).map((ans, idx) => (
                <li key={idx}>
                  {ans} {canEdit && <IconButton data-testid='remove-answer' iconProps={{iconName: 'Delete'}} onClick={() => handleRemoveAnswer(idx)}/>}
                </li>
              ))}
            </ul>
            {canEdit &&
            <React.Fragment>
                <input data-testid='add-answer-input' value={newAnswer} onChange={e => setNewAnswer(e.target.value)} placeholder="Add answer" />
                <IconButton data-testid='add-answer-button' iconProps={{iconName: 'Add'}} onClick={handleAddAnswer}/>
            </React.Fragment>
            }
            
        </div>
    </div>
  );
};

export default StorefrontQuestionEditor;

